<template>
  <v-app :theme="theme">
    <Announcement />
    <Sidebar ref="sidebarRef" />
    <Navbar
      v-if="$route && !routeMeta.hideNavbar"
      :hide-sidebar="routeMeta.hideSidebar"
      :on-back="routeMeta.onBack"
      @toggle="onToggle"
    />
    <v-main class="h-screen overflow-y-auto">
      <router-view />
    </v-main>
    <Snackbar />
    <Dialog />
    <TelemetryDialog v-model="isRequestingSupport" />
  </v-app>
</template>

<script lang="ts">
import { isJWTValid } from '@policyfly/utils/api'
import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { useColorMode, useTitle, useWindowFocus } from '@vueuse/core'
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useApplicationStore } from './stores/application'
import { useAttachmentStore } from './stores/attachment'
import { useSupport } from '@/composables/support'
import { useVersion } from '@/composables/version'
import { useApiStore } from '@/stores/api'
import { useAuthenticationStore } from '@/stores/authentication'
import { useGlobalStore } from '@/stores/global'
import { useWebSocketStore } from '@/stores/webSocket'
import Announcement from 'components/common/Announcement/Announcement.vue'
import Navbar from 'components/common/Navbar/Navbar.vue'
import Sidebar from 'components/common/Sidebar/Sidebar.vue'
import Dialog from 'components/global/Notification/Dialog.vue'
import Snackbar from 'components/global/Notification/Snackbar.vue'
import TelemetryDialog from 'components/global/Notification/TelemetryDialog.vue'

import routeNames from '@/router/routeNames'

import type { StrictRouteConfig } from 'types/router'

export default defineComponent({
  name: 'App',

  components: {
    Announcement,
    Navbar,
    Sidebar,
    Snackbar,
    // oxlint-disable-next-line vue/no-reserved-component-names
    Dialog,
    TelemetryDialog,
  },

  setup () {
    const apiStore = useApiStore()
    const authenticationStore = useAuthenticationStore()
    const applicationStore = useApplicationStore()
    const attachmentStore = useAttachmentStore()
    const router = useRouter()
    async function loadAccount (): Promise<void> {
      try {
        await authenticationStore.load()
      } catch (err) {
        // sent when bingoId is expected but missing, workaround for when bingo flag is enabled
        if (err instanceof RpcError && err.code === GrpcStatusCode[GrpcStatusCode.INVALID_ARGUMENT]) {
          console.error('BingoId is missing, logging out user')
          await authenticationStore.logout()
          await router.replace({ name: routeNames.LOGIN })
        } else {
          console.error('Failed to load user details', err)
        }
      }
    }
    if (apiStore.isLoggedIn) {
      loadAccount()
    }

    const sidebarRef = ref<{ toggle(): void } | null>(null)
    function onToggle (): void {
      sidebarRef.value?.toggle()
    }

    const { isRequestingSupport } = useSupport()

    const route = useRoute()
    const routeMeta = computed<NonNullable<StrictRouteConfig['meta']>>(() => route.meta ?? {})

    const globalStore = useGlobalStore()
    const mode = useColorMode()
    const theme = computed(() => {
      const m = mode.value
      if (globalStore.isBetaEnv) {
        switch (m) {
          case 'light': return 'pinkLight'
          case 'dark': return 'pinkDark'
        }
      }
      return m
    })

    const { checkSpaVersion } = useVersion()
    async function checkVersion (): Promise<void> {
      try {
        const { version } = await apiStore.environment.version()
        await checkSpaVersion(version)
      } catch {}
    }

    const focused = useWindowFocus()
    let checkOnFocus = false
    watch(focused, async (value) => {
      if (value && checkOnFocus) {
        checkOnFocus = false
        await checkVersion()
      }
    })

    onMounted(async () => {
      while (true) {
        if (focused.value) {
          await checkVersion()
        } else {
          checkOnFocus = true
        }
        await new Promise((resolve) => window.setTimeout(resolve, 5000 * 60))
      }
    })

    useTitle(computed(() => {
      const parts = ['PolicyFly']
      if (authenticationStore.currentProgram) {
        parts.unshift(authenticationStore.currentProgram.slug.toUpperCase())
      }
      if (routeMeta.value.applicationLayout) {
        parts.unshift(applicationStore.PNI || 'Application')
      }
      if (routeMeta.value.title) {
        // use selected attachment name if available
        if (route.params.attachmentId && route.name && ([
          routeNames.APPLICATION_ATTACHMENTS,
          routeNames.APPLICATION_POLICY_DOCS,
          routeNames.POLICY_ATTACHMENTS,
          routeNames.POLICY_POLICY_DOCS,
        ] as string[]).includes(route.name as string)) {
          const attachment = (route.meta.policyView
            ? attachmentStore.policyAttachments
            : attachmentStore.applicationAttachments).find((a) => String(a.id) === route.params.attachmentId)
          parts.unshift(attachment?.filename || String(routeMeta.value.title))
        } else {
          parts.unshift(String(routeMeta.value.title))
        }
      }
      return parts.join(' - ')
    }))

    const { connect, disconnect } = useWebSocketStore()
    watch([
      () => authenticationStore.loaded,
      () => apiStore.accessToken,
    ],
    ([loaded, token]) => {
      if (loaded && isJWTValid(token)) {
        connect()
      } else {
        disconnect()
      }
    },
    )
    onBeforeUnmount(() => {
      disconnect()
    })

    return {
      sidebarRef,
      onToggle,

      isRequestingSupport,

      routeMeta,

      theme,
    }
  },
})
</script>

<!-- eslint-disable-next-line vue/enforce-style-attribute -->
<style lang="sass">
.no-top-border-radius
  border-radius: 0px 0px 4px 4px
.attachments-menu .v-menu--inline
  display: none
</style>
